/**
 * Generated by: /create-workflow-model http://localhost:4300/proxy/member/v1/selfservice-actions/criticalIllness --name critical-illness --update --externalId 356719
 * Generated on: 2025-01-15
 * Last updated: 2026-05-07
 *
 * This file was auto-generated. To regenerate with latest workflow data, run the command above.
 */

import { Injectable } from '@angular/core';
import {
  formFieldBoolean,
  formFieldDate,
  formFieldString,
  ObjectWorkflowIntegrationFormField,
  TypedFormField,
  TypedFormTable,
  WorkflowDocument,
  WorkflowIntegrationResponse,
  WorkflowIntegrationStep,
} from '@cassiopeia/integration';
import { RemoveIndexSignature, requiredValue } from '@nestech/utils';
import { ConsentData, SelfServiceWorkflowApi } from '../danish-pension';

// Type aliases for domain-specific usage
export type CriticalIllnessStep = WorkflowIntegrationStep;
export type CriticalIllnessFormField = ObjectWorkflowIntegrationFormField;

export type PaymentMethod = 'nemKonto' | 'accountNumber';

// Step ID union type
export type CriticalIllnessStepId = 'illnessType' | 'treatment' | 'uploadJournals' | 'uploadDocuments';
export type IllnessTypeFormFieldKey = keyof RemoveIndexSignature<IllnessTypeStep['formFields']>;

// IllnessType step form fields interface
export interface IllnessTypeFormFields extends Record<string, ObjectWorkflowIntegrationFormField> {
  criticalIllnessDiagnosis: TypedFormField<string>;
  illnessComment: TypedFormField<string>;
  illnessDiagnosisDate: TypedFormField<Date>;
  hasPreviousCriticalIllness: TypedFormField<boolean>;
  previousCriticalIllnessDiagnosis: TypedFormField<string>;
  previousIllnessComment: TypedFormField<string>;
  previousIllnessDiagnosisDate: TypedFormField<Date>;
}

// IllnessType step interface with typed form fields
export interface IllnessTypeStep extends Omit<WorkflowIntegrationStep, 'formFields' | 'metaData'> {
  formFields: IllnessTypeFormFields;
  metaData?: Record<'notCoveredBy', string[]>;
}

// Hospital info table row
export interface HospitalInfo {
  readonly id?: string;
  hospitalName: string;
  hospitalDepartment: string;
  hospitalAddress: string;
  hospitalPostalCode: string;
  hospitalCity: string;
  hospitalCountry: string;
}

// Treatment step form tables interface
export interface TreatmentFormTables {
  hospitalInfo: TypedFormTable<HospitalInfo>;
}

// Treatment step interface with typed form table
export interface TreatmentStep extends Omit<WorkflowIntegrationStep, 'formTables'> {
  formTables: TreatmentFormTables;
}

// UploadDocuments step form fields interface
export interface UploadDocumentsFormFields extends Record<string, ObjectWorkflowIntegrationFormField> {
  consentGiven: TypedFormField<boolean>;
  useNemKonto: TypedFormField<boolean>;
  accountNumber: TypedFormField<string>;
  updateLegalPartyPayOut: TypedFormField<boolean>;
  postponeAccountDocumentation: TypedFormField<boolean>;
}

// UploadDocuments step interface with typed form fields
export interface UploadDocumentsStep extends Omit<WorkflowIntegrationStep, 'formFields'> {
  formFields: UploadDocumentsFormFields;
  metaData: {
    consentData: ConsentData;
  };
  documents?: WorkflowDocument[] | null;
}

export interface CriticalIllness extends WorkflowIntegrationResponse {
  illnessType: IllnessTypeStep;
  treatment: TreatmentStep;
  uploadJournals?: WorkflowIntegrationStep;
  uploadDocuments: UploadDocumentsStep;
}

/**
 * Maps the raw WorkflowIntegrationResponse from the integration API
 * to a more strongly-typed CriticalIllness interface
 */
const mapApiToCriticalIllness = (api: WorkflowIntegrationResponse): CriticalIllness => {
  const illnessTypeStep = api.steps?.['illnessType'];
  const treatmentStep = api.steps?.['treatment'];
  const uploadDocumentsStep = api.steps?.['uploadDocuments'];
  const consentData = requiredValue<ConsentData>(
    uploadDocumentsStep?.metaData?.['consentData'],
    'consentData',
  ) as ConsentData;

  if (!illnessTypeStep) throw new Error('illnessType step is required');
  if (!treatmentStep) throw new Error('treatment step is required');
  if (!uploadDocumentsStep) throw new Error('uploadDocuments step is required');

  return {
    ...api,
    illnessType: {
      ...illnessTypeStep,
      formFields: {
        criticalIllnessDiagnosis: formFieldString(
          'criticalIllnessDiagnosis',
          illnessTypeStep.formFields,
        ) as TypedFormField<string>,
        illnessComment: formFieldString('illnessComment', illnessTypeStep.formFields),
        illnessDiagnosisDate: formFieldDate('illnessDiagnosisDate', illnessTypeStep.formFields),
        hasPreviousCriticalIllness: formFieldBoolean('hasPreviousCriticalIllness', illnessTypeStep.formFields),
        previousCriticalIllnessDiagnosis: formFieldString(
          'previousCriticalIllnessDiagnosis',
          illnessTypeStep.formFields,
        ) as TypedFormField<string>,
        previousIllnessComment: formFieldString('previousIllnessComment', illnessTypeStep.formFields),
        previousIllnessDiagnosisDate: formFieldDate('previousIllnessDiagnosisDate', illnessTypeStep.formFields),
      },
    },
    treatment: {
      ...treatmentStep,
      formTables: {
        hospitalInfo: {
          ...treatmentStep.formTables?.['hospitalInfo'],
          tableData:
            treatmentStep.formTables?.['hospitalInfo']?.tableData?.map(row => ({
              id: row['id'],
              hospitalName: row['hospitalName'],
              hospitalDepartment: row['hospitalDepartment'],
              hospitalAddress: row['hospitalAddress'],
              hospitalPostalCode: row['hospitalPostalCode'],
              hospitalCity: row['hospitalCity'],
              hospitalCountry: row['hospitalCountry'],
            })) || [],
        } as TypedFormTable<HospitalInfo>,
      },
    },
    uploadDocuments: {
      ...uploadDocumentsStep,
      formFields: {
        consentGiven: formFieldBoolean('consentGiven', uploadDocumentsStep.formFields),
        useNemKonto: formFieldBoolean('useNemKonto', uploadDocumentsStep.formFields),
        updateLegalPartyPayOut: formFieldBoolean('updateLegalPartyPayOut', uploadDocumentsStep.formFields),
        accountNumber: formFieldString('accountNumber', uploadDocumentsStep.formFields),
        postponeAccountDocumentation: formFieldBoolean('postponeAccountDocumentation', uploadDocumentsStep.formFields),
      },
      metaData: {
        ...uploadDocumentsStep.metaData,
        consentData: {
          ...consentData,
          consentDetails: requiredValue(consentData.consentDetails, 'consentDetails'),
          consentHeader: requiredValue(consentData.consentHeader, 'consentHeader'),
          briefDescription: requiredValue(consentData.briefDescription, 'briefDescription'),
          readMoreLabel: requiredValue(consentData.readMoreLabel, 'readMoreLabel'),
          briefTitle: consentData.briefTitle,
        },
      },
    },
  };
};

@Injectable()
export class CriticalIllnessApi extends SelfServiceWorkflowApi<CriticalIllness> {
  override mapToModel(response: WorkflowIntegrationResponse): CriticalIllness {
    return mapApiToCriticalIllness(response);
  }

  override actionName = 'criticalIllness';
}
